import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import _ from 'lodash'
import { LbHttpService } from '../lb-http.service'
import { LocalStorageService } from '../utils/local-storage.service'
import { MatDialog } from '@angular/material/dialog'
import { ApplicationsService } from '../applications.service'
import { ErrorService } from '../utils/error.service'
import { ConfigService } from '../config.service'
import { EventSourcePolyfill, NativeEventSource } from 'event-source-polyfill'
import { HttpClient } from '@angular/common/http'
import { L10N_LOCALE, L10nLocale } from 'angular-l10n'
import { QueueConfigService } from './queue-config.service'

const EventSource = NativeEventSource || EventSourcePolyfill

@Injectable( {
    providedIn: 'root'
} )
export class QueueStateService implements OnDestroy {

    queueStateSubscription: any
    queueStateSseRetry: number = 0

    currentsQueuesStateSse = null

    private queueStateLoaded: boolean = false
    private queueStateEvent: Subject<any> = new Subject<any>()
    public $queueStateEvent: Observable<any> = new Observable<any>()
    public queueStates: any = {}

    constructor (
        @Inject( L10N_LOCALE ) public locale: L10nLocale,
        private zone: NgZone,
        private http: HttpClient,
        public lbHttpService: LbHttpService,
        public applicationsService: ApplicationsService,
        public localStorageService: LocalStorageService,
        public queueConfigService: QueueConfigService,
        public configService: ConfigService,
        public dialog: MatDialog,
        private errorService: ErrorService
    ) {
        this.$queueStateEvent = this.queueStateEvent.asObservable()
    }

    ngOnDestroy (): void {
        this.unsubscribe()
    }

    public unsubscribe () {
        this.queueStateLoaded = false
        if ( this.queueStateSubscription ) {
            this.queueStateSubscription.unsubscribe()
        }

        if (this.currentsQueuesStateSse) {
            this.currentsQueuesStateSse.close()
            this.currentsQueuesStateSse = null
        }
    }

    async loadQueueStateIfNotLoaded ( queuesId ) {
        if ( !this.queueStateLoaded ) {
            await this.initSubScribe( queuesId )
        } else {
            this.sendQueueStates()
        }
    }

    public sendQueueStates () {
        if ( Object.keys( this.queueStates ).length > 0 ) {
            for ( const queueId in this.queueStates ) {
                if ( Object.keys( this.queueStates[queueId] ).length > 0 ) {
                    this.queueStateEvent.next( { queueId: queueId, queueState: this.queueStates[queueId] } )
                }
            }
        }
    }

    async initSubScribe ( queuesId ) {
        const version = await (this.queueConfigService.getQueueVersion( queuesId[0] ).toPromise())

        this.unsubscribe()
        this.queueStateLoaded = true

        queuesId = _.uniq( queuesId )
        queuesId = _.filter( queuesId, ( q ) => {
            return q && q !== ''
        } )
        for ( const queueId of queuesId ) {
            this.queueStates[queueId] = {}
        }

        // GET TICKETS WITH SSE
        this.queueStateSubscription = (await this.httpGetQueuesStateSse( queuesId )).subscribe( async ( res ) => {
            this.errorService.logDebug( 'Event on queue state SSE', res )
            this.queueStateSseRetry = 0
            if ( res ) {
                await this.updateQueueStateFromEvent( res )
            }
        }, async ( error ) => {
            this.errorService.logError( 'Error while getting current tickets by SSE', error )

            if ( error === 'SSE_TEMPORARY_ERROR' || error === 'SSE_CLOSED' ) {
                this.queueStateSseRetry++

                if ( this.queueStateSseRetry < 5 ) {
                    await this.initSubScribe( queuesId )
                } else {
                    this.unsubscribe()
                    this.errorService.reloadInterface()
                }
            }
        } )
    }

    updateQueueStateFromEvent ( event ) {
        if ( event.resource === 'QUEUE' ) {
            if ( event.actionCategory === 'ALL_QUEUE_STATE' ) {
                if ( event.data ) {
                    for ( const queueId in event.data ) {
                        if ( this.queueStates[queueId] ) {
                            this.queueStates[queueId] = event.data[queueId]
                        }
                    }
                    this.sendQueueStates()
                }
            } else if ( event.actionCategory === 'UPDATE_QUEUE_STATE' ) {
                if ( event.data && event.data.queueId && this.queueStates[event.data.queueId] ) {
                    this.queueStates[event.data.queueId] = event.data.queueState
                    this.sendQueueStates()
                }
            }

        }
    }

    async httpGetQueuesStateSse ( queues ) {

        const version = await ( this.queueConfigService.getQueueVersion( queues[0] ).toPromise())
        let url
        if ( version === 1 ) {
            url = this.configService.getQueuesStateSseV1
        } else {
            url = this.configService.getQueuesStateSseV2
        }
        const httpOptions = await this.lbHttpService.generateHeaderForLineberty( false, true, 'booking' )
        httpOptions['params']['queuesId'] = queues

        return Observable.create( ( observer ) => {
            this.currentsQueuesStateSse = new EventSourcePolyfill( this.lbHttpService.applyGetParamsToUrl( url, httpOptions.params ), {
                headers: httpOptions.headers
            } )
            this.currentsQueuesStateSse.onopen = () => {
                this.errorService.logDebug( 'Queue state SSE connected' )
            }
            this.currentsQueuesStateSse.onmessage = ( event ) => {
                this.zone.run( () => observer.next( JSON.parse( event.data ) ) )
            }
            this.currentsQueuesStateSse.onerror = ( error ) => {
                this.errorService.logError( 'Error on queue state sse', error )
                this.zone.run( () => observer.error( 'SSE_CLOSED' ) )
            }
        } )
    }

}
